import type { DetectionParams } from '@audigoal/core';
import { CHORD_SPECTRUM_SIZE, FrameAnalyzer, type AnalysisFrame } from './frame-analyzer';
import { NoteTracker, type NoteEvent } from './note-tracker';
import { OnsetDetector, type Onset } from './onset-detector';

/**
 * The live microphone (plan doc 11).
 *
 * Owns the permission flow, the audio graph and the worklet, and turns the
 * frames they produce into the same events `runOffline` produces from a buffer.
 * Everything above this point — matchers, chord detection, the UI — is
 * therefore identical whether the audio came from a microphone or a file.
 */

export type MicState = 'idle' | 'requesting' | 'running' | 'denied' | 'unavailable' | 'error';

export interface MicEngineOptions {
  /** Injected in tests; defaults to the real one. */
  getUserMedia?: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
  /** Injected in tests. */
  createContext?: () => AudioContext;
  /** Force the main-thread path, for devices without AudioWorklet. */
  forceFallback?: boolean;
  workletUrl?: URL;
}

/**
 * What calibration measured for this learner, room and device.
 *
 * Kept as one object rather than loose arguments so a caller either passes
 * everything that was measured or nothing at all; a half-applied profile is the
 * kind of thing that produces a detector nobody can reason about.
 */
export interface MicTuning {
  a4Hz?: number;
  transposeSemis?: number;
  /** Measured noise gate, as an amplitude. */
  gate?: number;
  /** Measured flux sensitivity for finding an onset. */
  onsetSensitivity?: number;
}

export interface MicListeners {
  onFrame?: (frame: AnalysisFrame) => void;
  onEvent?: (event: NoteEvent) => void;
  onOnset?: (onset: Onset) => void;
  onState?: (state: MicState, detail?: string) => void;
}

/**
 * The constraints matter. Browser DSP is tuned for speech: echo cancellation
 * and noise suppression eat a plucked transient, and automatic gain control
 * rides over the decay the detector uses to tell a note has ended.
 */
export const MIC_CONSTRAINTS: MediaStreamConstraints = {
  audio: {
    echoCancellation: false,
    noiseSuppression: false,
    autoGainControl: false,
    channelCount: 1,
    sampleRate: { ideal: 48000 },
  },
  video: false,
};

const FALLBACK_BUFFER = 4096;

export class MicEngine {
  private state: MicState = 'idle';
  private stream: MediaStream | null = null;
  private ctx: AudioContext | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private highpass: BiquadFilterNode | null = null;
  private node: AudioWorkletNode | ScriptProcessorNode | null = null;
  private sink: GainNode | null = null;

  private tracker: NoteTracker | null = null;
  private onsets: OnsetDetector | null = null;
  private fallbackAnalyzer: FrameAnalyzer | null = null;
  private fallbackCarry = 0;

  private detection: DetectionParams | null = null;
  private tuning: MicTuning = {};
  private wantSpectrum = false;
  /** Detection is suspended while the app plays a prompt through the speaker. */
  private duckedUntil = 0;

  constructor(
    private readonly listeners: MicListeners = {},
    private readonly options: MicEngineOptions = {},
  ) {}

  get currentState(): MicState {
    return this.state;
  }

  get sampleRate(): number {
    return this.ctx?.sampleRate ?? 48000;
  }

  /**
   * Seconds on the microphone's own clock, the one every event's `t` is on.
   *
   * The microphone and the playback engine each own an AudioContext, and the
   * two clocks start at different moments. Anything that needs to place a
   * heard note against something being played — the song highway — samples
   * both clocks at the same instant and converts through the difference.
   */
  now(): number {
    return this.ctx?.currentTime ?? 0;
  }

  /** What the browser actually gave us, which is not always what was asked for. */
  get trackSettings(): MediaTrackSettings | null {
    return this.stream?.getAudioTracks()[0]?.getSettings() ?? null;
  }

  private setState(state: MicState, detail?: string): void {
    this.state = state;
    this.listeners.onState?.(state, detail);
  }

  /** True when this browser can capture audio at all. */
  static isSupported(): boolean {
    return (
      typeof navigator !== 'undefined' &&
      navigator.mediaDevices !== undefined &&
      typeof navigator.mediaDevices.getUserMedia === 'function'
    );
  }

  /**
   * Asks for the microphone and starts listening.
   *
   * Must be called from a user gesture: iOS requires one both for the
   * permission prompt and for resuming the AudioContext, and it must be the
   * same gesture.
   */
  async start(detection: DetectionParams, options: MicTuning = {}): Promise<boolean> {
    if (this.state === 'running') {
      this.configure(detection, options);
      return true;
    }
    // An injected capture function is support, whatever the host says it has.
    // Without this the injection point exists but cannot be used: the engine
    // refuses before it ever reaches the function it was handed.
    if (this.options.getUserMedia === undefined && !MicEngine.isSupported()) {
      this.setState('unavailable', 'This browser cannot record audio.');
      return false;
    }

    this.setState('requesting');
    const getUserMedia =
      this.options.getUserMedia ??
      ((constraints: MediaStreamConstraints) => navigator.mediaDevices.getUserMedia(constraints));

    try {
      this.stream = await getUserMedia(MIC_CONSTRAINTS);
    } catch (error) {
      const name = error instanceof Error ? error.name : '';
      // A refusal and a broken device need different advice, so they are
      // different states rather than one generic failure.
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        this.setState('denied', 'Microphone permission was declined.');
      } else if (name === 'NotFoundError' || name === 'OverconstrainedError') {
        this.setState('unavailable', 'No microphone was found.');
      } else {
        this.setState('error', error instanceof Error ? error.message : String(error));
      }
      return false;
    }

    try {
      await this.buildGraph(detection, options);
      this.setState('running');
      return true;
    } catch (error) {
      this.stop();
      this.setState('error', error instanceof Error ? error.message : String(error));
      return false;
    }
  }

  private async buildGraph(detection: DetectionParams, options: MicTuning): Promise<void> {
    const stream = this.stream;
    if (stream === null) throw new Error('No stream');

    const ctx = this.options.createContext?.() ?? new AudioContext();
    this.ctx = ctx;
    if (ctx.state === 'suspended') await ctx.resume();
    // A fresh context starts its clock near zero, and the duck deadline was
    // measured on the clock of the context before it. Carried over, it reads
    // as "ignore everything for the next minute and a half": the microphone
    // runs, the level meter moves, and not one frame reaches the detector.
    // The deadline belongs to the clock it was taken from, so it goes when
    // that clock does.
    this.duckedUntil = 0;

    this.configure(detection, options);

    this.source = ctx.createMediaStreamSource(stream);
    // Rolls off handling noise and room rumble below anything an instrument
    // plays; the guitar's low E at 82 Hz is comfortably above it.
    this.highpass = ctx.createBiquadFilter();
    this.highpass.type = 'highpass';
    this.highpass.frequency.value = 60;
    this.highpass.Q.value = 0.7;

    const useWorklet = this.options.forceFallback !== true && ctx.audioWorklet !== undefined;

    if (useWorklet) {
      await this.buildWorklet(ctx, detection);
    } else {
      this.buildFallback(ctx, detection);
    }

    // A zero-gain sink. Safari stops pulling on a node that reaches nothing, so
    // without this the worklet simply stops being called.
    this.sink = ctx.createGain();
    this.sink.gain.value = 0;

    this.source.connect(this.highpass);
    this.highpass.connect(this.node as AudioNode);
    (this.node as AudioNode).connect(this.sink);
    this.sink.connect(ctx.destination);

    // A device unplugged mid-exercise ends the track rather than erroring.
    for (const track of stream.getAudioTracks()) {
      track.addEventListener('ended', () => {
        this.setState('error', 'The microphone was disconnected.');
      });
    }
  }

  private async buildWorklet(ctx: AudioContext, detection: DetectionParams): Promise<void> {
    const url = this.options.workletUrl ?? new URL('./pitch.worklet.ts', import.meta.url);
    await ctx.audioWorklet.addModule(url);
    const node = new AudioWorkletNode(ctx, 'pitch-processor', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      channelCount: 1,
      processorOptions: {
        windowSize: detection.windowSize,
        hopSize: detection.hopSize,
        clarityThreshold: detection.clarityThreshold,
        minFreq: detection.minFreq,
        maxFreq: detection.maxFreq,
        wantSpectrum: this.wantSpectrum,
      },
    });
    node.port.onmessage = (event: MessageEvent<AnalysisFrame>) => this.handleFrame(event.data);
    this.node = node;
  }

  /**
   * The main-thread path, for browsers without AudioWorklet. It runs the same
   * analyzer, so results agree; it just competes with rendering for time.
   */
  private buildFallback(ctx: AudioContext, detection: DetectionParams): void {
    const processor = ctx.createScriptProcessor(FALLBACK_BUFFER, 1, 1);
    this.fallbackAnalyzer = new FrameAnalyzer({
      sampleRate: ctx.sampleRate,
      windowSize: detection.windowSize,
      clarityThreshold: detection.clarityThreshold,
      minFreq: detection.minFreq,
      maxFreq: detection.maxFreq,
    });

    const ring = new Float32Array(detection.windowSize);
    let writeIndex = 0;
    // The chord spectrum needs more history than the tracking window holds.
    const longRing = new Float32Array(CHORD_SPECTRUM_SIZE);
    let longWriteIndex = 0;

    processor.onaudioprocess = (event: AudioProcessingEvent) => {
      const input = event.inputBuffer.getChannelData(0);
      for (let i = 0; i < input.length; i += 1) {
        const sample = input[i] ?? 0;
        ring[writeIndex] = sample;
        writeIndex = (writeIndex + 1) % detection.windowSize;
        longRing[longWriteIndex] = sample;
        longWriteIndex = (longWriteIndex + 1) % CHORD_SPECTRUM_SIZE;
        this.fallbackCarry += 1;
        if (this.fallbackCarry < detection.hopSize) continue;
        this.fallbackCarry = 0;

        const window = new Float32Array(detection.windowSize);
        for (let j = 0; j < detection.windowSize; j += 1) {
          window[j] = ring[(writeIndex + j) % detection.windowSize] ?? 0;
        }
        let longWindow: Float32Array | undefined;
        if (this.wantSpectrum) {
          longWindow = new Float32Array(CHORD_SPECTRUM_SIZE);
          for (let j = 0; j < CHORD_SPECTRUM_SIZE; j += 1) {
            longWindow[j] = longRing[(longWriteIndex + j) % CHORD_SPECTRUM_SIZE] ?? 0;
          }
        }
        const frame = this.fallbackAnalyzer?.analyze(
          window,
          ctx.currentTime,
          this.wantSpectrum,
          longWindow,
        );
        if (frame !== undefined) this.handleFrame(frame);
      }
    };
    this.node = processor;
  }

  private handleFrame(frame: AnalysisFrame): void {
    // Ignore our own speaker. Without this the app hears the prompt it just
    // played and scores it as the learner's answer.
    if (frame.t * 1000 < this.duckedUntil) return;

    this.listeners.onFrame?.(frame);
    const events = this.tracker?.push(frame) ?? [];
    for (const event of events) this.listeners.onEvent?.(event);
    const onset = this.onsets?.push(frame) ?? null;
    if (onset !== null) this.listeners.onOnset?.(onset);
  }

  /** Re-tunes for a different instrument without tearing the graph down. */
  configure(detection: DetectionParams, options: MicTuning = {}): void {
    this.detection = detection;
    this.tuning = options;
    this.tracker = new NoteTracker({
      detection,
      sampleRate: this.sampleRate,
      ...(options.a4Hz !== undefined ? { a4Hz: options.a4Hz } : {}),
      ...(options.transposeSemis !== undefined ? { transposeSemis: options.transposeSemis } : {}),
      ...(options.gate !== undefined ? { measuredGate: options.gate } : {}),
    });
    this.onsets = new OnsetDetector(
      options.onsetSensitivity !== undefined ? { sensitivity: options.onsetSensitivity } : {},
    );

    const node = this.node;
    if (node !== null && 'port' in node) {
      node.port.postMessage({
        type: 'config',
        windowSize: detection.windowSize,
        hopSize: detection.hopSize,
        clarityThreshold: detection.clarityThreshold,
        minFreq: detection.minFreq,
        maxFreq: detection.maxFreq,
        wantSpectrum: this.wantSpectrum,
      });
    }
  }

  /** Chord exercises need the spectrum; asking for it costs bandwidth. */
  setWantSpectrum(want: boolean): void {
    this.wantSpectrum = want;
    const node = this.node;
    if (node !== null && 'port' in node) {
      node.port.postMessage({ type: 'config', wantSpectrum: want });
    }
  }

  /**
   * Stops listening for a moment, while a prompt plays through the speaker.
   *
   * Does nothing when there is no clock to measure against: a prompt that
   * plays before the microphone is open cannot be heard by it, and a deadline
   * set from a missing context is a deadline on the next context's clock.
   */
  duckFor(ms: number): void {
    if (this.ctx === null) return;
    this.duckedUntil = this.ctx.currentTime * 1000 + ms + 300;
  }

  /** Forgets the room and the current note; used between exercises. */
  reset(): void {
    this.tracker?.reset();
    this.onsets?.reset();
  }

  stop(): void {
    this.node?.disconnect();
    this.sink?.disconnect();
    this.highpass?.disconnect();
    this.source?.disconnect();
    for (const track of this.stream?.getTracks() ?? []) track.stop();

    if (this.node !== null && 'port' in this.node) this.node.port.onmessage = null;
    this.node = null;
    this.sink = null;
    this.highpass = null;
    this.source = null;
    this.stream = null;
    void this.ctx?.close();
    this.ctx = null;
    this.duckedUntil = 0;
    this.tracker = null;
    this.onsets = null;
    if (this.state === 'running') this.setState('idle');
  }

  /** Input devices, which can only be named once permission exists. */
  static async devices(): Promise<MediaDeviceInfo[]> {
    if (!MicEngine.isSupported()) return [];
    const all = await navigator.mediaDevices.enumerateDevices();
    return all.filter((d) => d.kind === 'audioinput');
  }

  get params(): DetectionParams | null {
    return this.detection;
  }

  /** What was measured for this setup, for anything that scores a spectrum. */
  get measured(): MicTuning {
    return this.tuning;
  }
}
